/**
 * The definition of Renderer and the functions that act on it
 *
 * Some of these functions are intended to be internal only. See the top-level `renderer.tsx`
 * to see which of these functions are exported as part of the Brenda interface.
 */
import * as BABYLON from '@babylonjs/core';
import { Engine, Scene } from '@babylonjs/core';
import * as GUI from '@babylonjs/gui';
import '@babylonjs/loaders';
import * as Gltf2 from '@babylonjs/loaders/glTF/2.0';
import EventEmitter from 'eventemitter3';
import * as Arr from 'fp-ts/Array';
import * as O from 'fp-ts/Option';
import * as TO from 'fp-ts/TaskOption';
import { constVoid, flow, pipe } from 'fp-ts/lib/function';
import { createArcRotateCamera, createSnapshotCamera } from './camera';
import { preventTouchEvents } from './canvas';
import { drawDimensionLines } from './dimensions';
import * as Event from './event';
import * as Furniture from './furniture';
import * as Modular from './modularFurniture';
import * as PT from './particleTree';
import * as P from './product';
import { scene } from './renderer/scene';
import * as S from './scene';
import * as ShadowRoom from './shadowRoom';
import * as T from './transform';
import { _ } from './type';
export { scene };
const createPBRSkybox = (options) => _.modify(_r => {
    S.createPBRSkybox(_r.scene, options);
    return _r;
});
export const create = (target, options) => {
    preventTouchEvents(target);
    const engine = new Engine(target, options.antialias);
    const scene = new Scene(engine);
    Gltf2.GLTFLoader.UnregisterExtension('KHR_materials_transmission');
    scene.useRightHandedSystem = true;
    scene.imageProcessingConfiguration.toneMappingEnabled = true;
    scene.imageProcessingConfiguration.toneMappingType =
        BABYLON.ImageProcessingConfiguration.TONEMAPPING_ACES;
    scene.imageProcessingConfiguration.contrast = options.contrast ?? 1.25;
    scene.imageProcessingConfiguration.exposure = options.exposure ?? 2.0;
    const events = new EventEmitter();
    if (options.handleResize && window.ResizeObserver) {
        const observer = new ResizeObserver(() => {
            engine.resize();
            if (scene.activeCamera) {
                setCameraPosition(scene);
            }
        });
        observer.observe(target);
    }
    engine.runRenderLoop(() => {
        scene.render();
    });
    // Considering moving all the extra bits like calling createArcRotateCamera and passing the bits around into something in the index.ts
    // for this package, and 'assembling' there. That leaves these functions simpler and performing a single activity, and means that we can use these methods easier without
    // cyclic dependencies elsewhere. Alternatively, the same kind of change but moving a lot of these into a renderer/renderer.ts file and leaving this file as the exported package
    return pipe({
        scene,
        events,
        attachmentPointsLayer: O.none,
        rotationHandles: options.rotationHandles,
    }, _.wrap, r => createArcRotateCamera(r, options), createSnapshotCamera, createPBRSkybox(options), 
    // TODO: split into more useful functions
    _.modify(_r => {
        // not going to capture this anywhere for now, see if it bites us later.
        S.createLights(_r.scene);
        if (options.ssao !== false) {
            S.createSsao(_r.scene);
        }
        // Once we begin to use Brenda for Space and Inspiration, may be good to create/destroy this layer depending
        // on whether it's actually needed to render the current scene
        const apLayer = new BABYLON.UtilityLayerRenderer(_r.scene);
        apLayer.utilityLayerScene.onPointerPick = (event, pickingInfo) => {
            if (pickingInfo?.hit &&
                pickingInfo.pickedMesh &&
                event.button === 0 &&
                pickingInfo.pickedMesh.name === 'attachmentPointSelectionSphere') {
                const [selectedOptionKey, selectedValue] = pickingInfo.pickedMesh.id.split(':');
                _r.events.emit(Event.Kind.OnSelectAttachmentOption, {
                    selectedOptionKey,
                    selectedValue,
                });
            }
        };
        /**
         * This is a bit of a hack to get the attachment point selection spheres to be hoverable. Very strange, but it seems to work.
         */
        apLayer.utilityLayerScene.onPointerObservable.add(pointerInfo => {
            try {
                // The part that gets it to work seems to be accessing the pickInfo
                // which is only generated when it is needed.
                pointerInfo.pickInfo?.pickedMesh?.name === 'attachmentPointSelectionSphere';
                // accessing it does cause an error to fire, but it seems to have
                // no effect on anything else - the try/catch is just to stop that
                // error from messing everything else up.
            }
            catch { }
        });
        apLayer.utilityLayerScene.attachControl();
        let lastPointerX;
        let lastPointerY;
        let isDragging = false;
        scene.onPointerDown = event => {
            // Record the initial pointer position
            lastPointerX = event.clientX;
            lastPointerY = event.clientY;
            // Set dragging to false at the start of each click
            isDragging = false;
        };
        scene.onPointerMove = event => {
            // Calculate the distance moved
            const deltaX = event.clientX - lastPointerX;
            const deltaY = event.clientY - lastPointerY;
            // If the pointer has moved,set dragging to true
            if (Math.abs(deltaX) > 1 || Math.abs(deltaY) > 1) {
                isDragging = true;
            }
        };
        scene.onPointerUp = (event, pickInfo) => {
            // Return early if picked a model
            if (pickInfo?.hit && pickInfo.pickedMesh?.name !== 'shadow-floor')
                return;
            // If it's a click event and not a drag event, trigger the action
            if (!isDragging) {
                _r.events.emit(Event.Kind.OnSceneClick);
            }
        };
        //TODO: find correct place for this
        S.createHighlightLayer(scene);
        return {
            ..._r,
            attachmentPointsLayer: O.some(apLayer),
        };
    }));
};
export const events = flow(_.unwrap, _r => _r.events);
export const canvas = (r) => pipe(r, _.unwrap, _r => _r.scene.getEngine().getRenderingCanvas());
export const emit = (r, event, data = []) => {
    pipe(r, _.unwrap, _r => _r.events.emit(event, ...data));
};
export const rotationHandles = flow(_.unwrap, _r => _r.rotationHandles);
const on = (r, e, fn) => pipe(r, events, ev => ev.on(e, fn), constVoid);
const off = (r, e, fn) => pipe(r, events, ev => ev.off(e, fn), constVoid);
export const onFurnitureModuleClick = (r, fn) => on(r, Event.Kind.OnFurnitureModuleClick, fn);
export const offFurnitureModuleClick = (r, fn) => on(r, Event.Kind.OnFurnitureModuleClick, fn);
export const onSelectAttachmentOption = (r, fn) => on(r, Event.Kind.OnSelectAttachmentOption, fn);
export const onSceneUserInteraction = (r, fn) => on(r, Event.Kind.OnSceneUserInteraction, fn);
export const offSelectAttachmentOption = (r, fn) => off(r, Event.Kind.OnSelectAttachmentOption, fn);
export const offSceneUserInteraction = (r, fn) => off(r, Event.Kind.OnSceneUserInteraction, fn);
export const onSceneClick = (r, fn) => {
    return on(r, Event.Kind.OnSceneClick, fn);
};
export const offSceneClick = (r, fn) => {
    return off(r, Event.Kind.OnSceneClick, fn);
};
export const attachmentPointsLayer = flow(_.unwrap, _r => _r.attachmentPointsLayer);
// TODO: magic strings
export const getSnapshotCamera = flow(scene, s => s.getCameraByName('snapshotCamera'), O.fromNullable);
export const getDefaultCamera = flow(scene, s => s.getCameraByName('defaultCamera'), O.fromNullable);
const isBabylonShadowGenerator = (g) => g instanceof BABYLON.ShadowGenerator;
/**
 * Get all native babylon shadow generators attached to scene lights
 */
export const getShadowGenerators = flow(scene, s => s.lights, Arr.filterMap(light => O.fromNullable(light.getShadowGenerator())), Arr.filter(isBabylonShadowGenerator));
export const setCameraPosition = (scene) => {
    pipe(O.Do, O.bind('rootTransform', () => O.fromNullable(T.findRoot(scene.transformNodes))), O.bind('boundingVectors', ({ rootTransform }) => O.fromNullable(rootTransform.getHierarchyBoundingVectors())), O.bind('camera', () => O.fromNullable(scene.getCameraByName('defaultCamera'))), O.bind('snapshotCamera', () => O.fromNullable(scene.getCameraByName('snapshotCamera'))), O.map(({ camera, snapshotCamera, boundingVectors, rootTransform }) => {
        // only set camera position if geometry has loaded, otherwise it will not be calculated correctly
        const hasGeometryLoaded = T.hasGeometryLoaded(rootTransform);
        if (hasGeometryLoaded) {
            // change distance of camera to fit the model
            const boundingSphere = new BABYLON.BoundingSphere(boundingVectors.min, boundingVectors.max);
            // change camera targets to the centre of the model
            const boundingBox = new BABYLON.BoundingBox(boundingVectors.min, boundingVectors.max);
            camera.lockedTarget = boundingBox.centerWorld;
            snapshotCamera.lockedTarget = boundingBox.centerWorld;
            // if the screen is taller than it is wide (aspect ratio < 1), constrain it to the width by multiplying by the ratio
            const aspectRatio = scene.getEngine().getAspectRatio(camera);
            const aspectMultiplier = Math.min(1, aspectRatio);
            // get the distance in meters required to fit the bounding sphere (with a + 50% padding border for the scene)
            const targetCameraDistance = (boundingSphere.radius * 1.2) / (Math.tan(camera.fov / 2) * aspectMultiplier);
            camera.radius = targetCameraDistance;
            // update maximum and minimum zoom to work with the constraints of the bounding sphere so we cant get 'inside' the model or zoom too far away
            camera.lowerRadiusLimit = boundingSphere.radius;
            camera.upperRadiusLimit =
                (boundingSphere.radius + 1) * (boundingSphere.radius + 1.5);
            const snapshotAspectRatio = scene.getEngine().getAspectRatio(snapshotCamera);
            const snapshotAspectMultiplier = Math.min(1, snapshotAspectRatio);
            const snapshotTargetCameraDistance = (boundingSphere.radius * 0.75) /
                (Math.tan(snapshotCamera.fov / 2) * snapshotAspectMultiplier);
            snapshotCamera.radius = snapshotTargetCameraDistance;
        }
    }));
};
export const takeSnapshot = (isDimensionsActive, measurementUnit, brandColor, dimensions) => (r) => {
    const _scene = scene(r);
    // Hide dimensions for the purposes of taking a snapshot.
    if (isDimensionsActive && brandColor !== undefined) {
        updateDimensions(r, false, brandColor, measurementUnit);
    }
    return pipe(r, getSnapshotCamera, TO.fromOption, TO.chain(camera => {
        const prevColor = _scene.clearColor;
        _scene.shadowsEnabled = false;
        // wait for the scene to be ready after removing shadows
        // otherwise the first call of this will result in a blank screenshot
        return TO.tryCatch(() => _scene.whenReadyAsync().then(async () => {
            const overlayMeshes = _scene.meshes.filter(mesh => mesh.renderOverlay);
            overlayMeshes.forEach(mesh => {
                mesh.renderOverlay = false;
            });
            // changing background colour after waiting otherwise it is a visible flash
            _scene.clearColor = new BABYLON.Color4(0, 0, 0, 0);
            const snapshot = BABYLON.Tools.CreateScreenshotUsingRenderTargetAsync(_scene.getEngine(), camera, { width: 415, height: 245, precision: 2 });
            overlayMeshes.forEach(mesh => {
                mesh.renderOverlay = true;
            });
            _scene.clearColor = prevColor;
            _scene.shadowsEnabled = true;
            if (isDimensionsActive && brandColor !== undefined) {
                updateDimensions(r, true, brandColor, measurementUnit, dimensions);
            }
            return snapshot;
        }));
    }));
};
export const createGuiLayer = flow(_.unwrap, _r => GUI.AdvancedDynamicTexture.CreateFullscreenUI('guiLayer', true, _r.scene));
// TODO: reconsider where this lives
export const createCameraInteractionHint = (r, gui, hintIconLocation) => {
    const hintIcon = new GUI.Image('interactionHint', hintIconLocation);
    const _r = _.unwrap(r);
    // TODO: magic string
    const camera = _r.scene.getCameraByName('defaultCamera');
    hintIcon.width = '96px';
    hintIcon.height = '96px';
    hintIcon.horizontalAlignment = GUI.Control.HORIZONTAL_ALIGNMENT_CENTER;
    hintIcon.verticalAlignment = GUI.Control.VERTICAL_ALIGNMENT_CENTER;
    hintIcon.alpha = 0;
    gui.addControl(hintIcon);
    const hintLength = 180;
    let active = false;
    let currentHintIconSwipeAnim;
    let currentCameraSwipeAnim;
    // set up keys for all animations
    const iconFadeKeys = [
        { frame: 0, value: 0.0 },
        { frame: 20, value: 1.0 },
        { frame: hintLength - 20, value: 1.0 },
        { frame: hintLength, value: 0.0 },
    ];
    const iconSwipeKeys = [
        { frame: 0, value: 0.0 },
        { frame: Math.floor(hintLength * 0.3), value: 70.0 },
        { frame: Math.floor(hintLength * 0.7), value: -70.0 },
        { frame: hintLength, value: 0.0 },
    ];
    // set up easing function and easing mode
    const easingFunction = new BABYLON.CubicEase();
    const easingMode = BABYLON.EasingFunction.EASINGMODE_EASEINOUT;
    easingFunction.setEasingMode(easingMode);
    // create animation clips
    const iconFadeAnim = new BABYLON.Animation('iconFade', 'alpha', 60, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    const iconSwipeAnim = new BABYLON.Animation('iconSwipe', 'left', 60, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
    // set up easing
    iconFadeAnim.setEasingFunction(easingFunction);
    iconSwipeAnim.setEasingFunction(easingFunction);
    // append keys to animation
    iconFadeAnim.setKeys(iconFadeKeys);
    iconSwipeAnim.setKeys(iconSwipeKeys);
    function play() {
        // TODO: handle properly
        if (camera === null || !(camera instanceof BABYLON.ArcRotateCamera)) {
            throw new Error('cannot find camera');
        }
        // set up keys for camera rotation starting at current camera.alpha
        const cameraSwipeKeys = [
            { frame: 0, value: camera.alpha },
            { frame: Math.floor(hintLength * 0.3), value: camera.alpha + 0.4 },
            { frame: Math.floor(hintLength * 0.7), value: camera.alpha - 0.4 },
            { frame: hintLength, value: camera.alpha },
        ];
        // set up animation and easing for camera swipe
        const cameraSwipeAnim = new BABYLON.Animation('cameraSwipe', 'alpha', 60, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
        cameraSwipeAnim.setEasingFunction(easingFunction);
        // append keys to animation
        cameraSwipeAnim.setKeys(cameraSwipeKeys);
        // play animations
        currentCameraSwipeAnim = _r.scene.beginDirectAnimation(camera, [cameraSwipeAnim], 0, hintLength, false, 1);
        currentHintIconSwipeAnim = _r.scene.beginDirectAnimation(hintIcon, [iconFadeAnim, iconSwipeAnim], 0, hintLength, false, 1, () => {
            active = false;
        });
        active = true;
    }
    function cancel() {
        if (active) {
            //stop current animations
            currentHintIconSwipeAnim.stop();
            currentCameraSwipeAnim.stop();
            // set up dehydrate keys
            const iconFadeoutKeys = [
                { frame: 0, value: hintIcon.alpha },
                { frame: 10, value: 0.0 },
            ];
            // create dehydrate animation and add keys
            const iconFadeoutAnim = new BABYLON.Animation('hintIconFadeout', 'alpha', 60, BABYLON.Animation.ANIMATIONTYPE_FLOAT, BABYLON.Animation.ANIMATIONLOOPMODE_CONSTANT);
            iconFadeoutAnim.setKeys(iconFadeoutKeys);
            // play animation
            _r.scene.beginDirectAnimation(hintIcon, [iconFadeoutAnim], 0, iconFadeoutKeys[iconFadeoutKeys.length - 1].frame, false, 1, () => {
                active = false;
            });
        }
    }
    return { play, cancel };
};
/**
 * This will only work for Configuration
 */
export const generateGltf = (r, download = false, filename, partToExclude) => pipe(scene(r), _s => S.generateGltf(_s, download, filename, partToExclude));
export const generateModelJson = (r) => pipe(scene(r), S.generateModelJson);
export const generateModularModelJson = (r) => pipe(scene(r), S.generateModularModelJson);
export const onError = (r, f) => {
    pipe(r, events, _e => {
        _e.on(Event.Kind.OnError, f);
    });
};
export const offError = (r, f) => pipe(r, events, _e => {
    _e.off(Event.Kind.OnError, f);
});
export const onLoadSuccess = (r, f) => pipe(r, events, _e => {
    _e.on(Event.Kind.OnLoadSuccess, f);
});
export const render = async (renderer, data, showDimensionLines, brandColour, measurementUnit = 'millimetre', dimensions) => {
    let renderResultPromise;
    if (Furniture.isFurniture(data)) {
        renderResultPromise = PT.render(renderer, data.particleTree);
    }
    else if (Modular.isModularFurniture(data)) {
        ShadowRoom.ShadowFloor.create(scene(renderer));
        renderResultPromise = Modular.render(renderer, data);
    }
    else {
        renderResultPromise = P.render(renderer, data);
    }
    const renderResult = await renderResultPromise;
    const _scene = scene(renderer);
    setCameraPosition(_scene);
    if (renderResult.newTransform) {
        drawDimensionLines(_scene, renderResult.newTransform, showDimensionLines, brandColour, measurementUnit, dimensions);
    }
    if (renderResult.newTransform !== null) {
        events(renderer).emit(Event.Kind.OnLoadSuccess);
    }
};
export const updateDimensions = (renderer, enabled, brandColour, measurementUnit = 'millimetre', dimensions) => {
    const rendererScene = scene(renderer);
    const productTransform = T.findRoot(rendererScene.transformNodes);
    if (productTransform) {
        drawDimensionLines(rendererScene, productTransform, enabled, brandColour, measurementUnit, dimensions);
    }
};
export const highlightLayer = (r) => {
    return O.fromNullable(scene(r).getHighlightLayerByName('highlightLayer'));
};
export async function addModel(renderer, parentNode, modelUrl) {
    const p = await BABYLON.SceneLoader.ImportMeshAsync(null, modelUrl);
    // SceneLoader imports the root as the 0th mesh, to which all other
    // meshes are attached as descendants. We therefore just add this mesh
    // to the parent to preserve child transforms.
    const mesh = p.meshes[0];
    mesh.parent = parentNode;
    // `addShadowCaster` is recursive, so we just use it to add the new
    // node tree to each shadow generator attached to a light, then
    // set every loaded mesh to receive shadows individually.
    getShadowGenerators(renderer).forEach(generator => generator.addShadowCaster(mesh, true));
    p.meshes.forEach(mesh => {
        mesh.receiveShadows = true;
    });
}
export const shutDown = (r) => {
    const _r = _.unwrap(r);
    const engine = _r.scene.getEngine();
    _r.events.removeAllListeners();
    pipe(_r.attachmentPointsLayer, O.map(l => l.dispose()));
    engine.dispose();
};
